import axios from "axios";
import { PutEffect, put, takeEvery } from "redux-saga/effects";
import { showError, Polygon } from "../components/commonUtilities";
import {
    AssetConfigInterface,
    AssetDataInterface,
    AssetListViewAttributesReceivedAction,
    DeleteProposedAssetsAction,
    EditAssetFormDataReceivedAction,
    GetAssetConfigDataAction,
    GetAssetDataAction,
    GetEditAssetFormDataAction,
    GetProposedAssetsAction,
    PostEditAssetDataAction,
    PublishProposedAssetsAction,
    ReceivedAssetConfigDataAction,
    ReceivedAssetDataAction,
    ReceivedPolygonAssetDataAction,
    storeProposedAssetsAction,
    storeAssetsAction,
    DELETE_PROPOSED_ASSETS,
    GET_ASSET_CONFIG_DATA,
    GET_ASSET_DATA,
    GET_ASSET_LIST_VIEW_ATTRIBUTES,
    GET_EDIT_ASSET_FORM_DATA,
    GET_PROPOSED_ASSETS,
    POST_EDIT_ASSET_DATA,
    PUBLISH_PROPOSED_ASSETS,
    ReceivedDefaultPolygonAssetDataAction,
    EditAssetDataAction,
    ToggleEditAssetPanelAction,
    ToggleEditPolygonAssetPanelAction,
    ToggleCreatePolygonAssetPanelAction,
    GET_ASSETS,
    GetAssetsAction
} from "../actions/Assets/types";
import {
    assetListViewAttributesReceived,
    editAssetDataAction,
    editAssetFormDataReceived,
    receivedAssetConfigData,
    receivedAssetData,
    receivedDefaultPolygonAssetData,
    storeProposedAssets,
    storeAssets,
    toggleCreatePolygonAssetPanel,
    toggleEditAssetPanel,
    toggleEditPolygonAssetPanel
} from "../actions/Assets";
import { GeneratorYieldType } from "interfaces/ActionReducer/GetMethod/GetResponse";

function* getAssetData(
    action: GetAssetDataAction
): Generator<
    | Promise<ReceivedDefaultPolygonAssetDataAction | ReceivedAssetDataAction>
    | PutEffect<{ payload: ReceivedDefaultPolygonAssetDataAction["payload"]; type: string }>,
    void,
    | ReceivedDefaultPolygonAssetDataAction["payload"]["assetData"]
    | ReceivedAssetDataAction["payload"]["assetData"]
> {
    try {
        let json = yield axios
            .get("/api/asset/", { params: action.payload.params })
            .then((response) => response.data);
        if (action.payload.params.type === Polygon) {
            yield put(receivedDefaultPolygonAssetData({ assetData: json }));
        } else {
            yield put(receivedAssetData({ assetData: json as AssetDataInterface }));
        }
    } catch (e) {
        showError(e);
    }
}

export function* getAssetDataSaga() {
    yield takeEvery(GET_ASSET_DATA, getAssetData);
}

function* getEditAssetFormData(
    action: GetEditAssetFormDataAction
): Generator<
    | Promise<EditAssetFormDataReceivedAction>
    | PutEffect<{ payload: EditAssetFormDataReceivedAction["payload"]; type: string }>,
    void,
    EditAssetFormDataReceivedAction["payload"]["formData"]
> {
    try {
        let json = yield axios
            .get(`/api/proposed-assets-group/get_form_skeleton/`, { params: action.payload.params })
            .then((response) => response.data);

        yield put(editAssetFormDataReceived({ formData: json }));
    } catch (e) {
        showError(e);
    }
}

export function* getEditAssetFormDataSaga() {
    yield takeEvery(GET_EDIT_ASSET_FORM_DATA, getEditAssetFormData);
}

function* postEditAssetData(
    action: PostEditAssetDataAction
): Generator<
    | Promise<EditAssetDataAction>
    | GeneratorYieldType<EditAssetDataAction["payload"]>
    | Promise<ToggleEditAssetPanelAction["payload"]>
    | GeneratorYieldType<ToggleEditAssetPanelAction["payload"]>
    | GeneratorYieldType<ToggleEditPolygonAssetPanelAction["payload"]>
    | GeneratorYieldType<ToggleCreatePolygonAssetPanelAction["payload"]>,
    void,
    any
> {
    try {
        const json = yield axios
            .post("/api/proposed-assets-group/", action.payload.formData)
            .then((res) => res.data);
        action.payload.afterSuccess();
        yield put(editAssetDataAction(json.assets));
        yield put(toggleEditAssetPanel({ isEditAssetPanelOpen: false }));
        yield put(toggleEditPolygonAssetPanel({ isEditPolygonAssetPanelOpen: false }));
        yield put(toggleCreatePolygonAssetPanel({ isCreatePolygonAssetPanelOpen: false }));
    } catch (e) {
        showError(e);
    }
}

export function* postEditAssetDataSaga() {
    yield takeEvery(POST_EDIT_ASSET_DATA, postEditAssetData);
}

function* getAssets(
    action: GetAssetsAction
): Generator<
    Promise<storeAssetsAction> | PutEffect<{ payload: storeAssetsAction["payload"]; type: string }>,
    void,
    storeAssetsAction["payload"]["params"]
> {
    let url = "/api/asset/?";
    const { params } = action.payload;
    Object.keys(params).forEach((prop: string) => {
        if (Array.isArray(params[prop])) {
            if (action && params && params[prop]) {
                let allFilters = params[prop]?.toString();
                url += `&${prop}=${allFilters}`;
            }
        } else {
            url += `&${prop}=${params[prop]}`;
        }
    });

    try {
        const json = yield axios.get(url).then((res) => res.data);
        yield put(storeAssets({ params: json }));
    } catch (e) {
        showError(e);
    }
}

export function* getAssetsSaga() {
    yield takeEvery(GET_ASSETS, getAssets);
}

function* getProposedAssets(
    action: GetProposedAssetsAction
): Generator<
    | Promise<storeProposedAssetsAction>
    | PutEffect<{ payload: storeProposedAssetsAction["payload"]; type: string }>,
    void,
    storeProposedAssetsAction["payload"]["params"]
> {
    let url = "/api/proposed-assets-group/?";
    const { params } = action.payload;
    Object.keys(params).forEach((prop: string) => {
        if (Array.isArray(params[prop])) {
            if (action && params && params[prop]) {
                let allFilters = params[prop]?.toString();
                url += `&${prop}=${allFilters}`;
            }
        } else {
            url += `&${prop}=${params[prop]}`;
        }
    });

    try {
        const json = yield axios.get(url).then((res) => res.data);
        yield put(storeProposedAssets({ params: json }));
    } catch (e) {
        showError(e);
    }
}

export function* getProposedAssetsSaga() {
    yield takeEvery(GET_PROPOSED_ASSETS, getProposedAssets);
}

function* getAssetConfigData(
    action: GetAssetConfigDataAction
): Generator<
    | Promise<ReceivedAssetConfigDataAction>
    | PutEffect<{ payload: ReceivedAssetConfigDataAction["payload"]; type: string }>,
    void,
    ReceivedAssetConfigDataAction["payload"]["assetConfigData"]
> {
    try {
        let json = yield axios
            .get("/api/assetconfig/", { params: action.payload.params })
            .then((response) => response.data);
        json.results.forEach((config: AssetConfigInterface) => {
            config.icon = config.icon + `&t=${Math.round(Math.random() * 10000)}`;
        });
        yield put(receivedAssetConfigData({ assetConfigData: json }));
    } catch (e) {
        showError(e);
    }
}

export function* getAssetConfigDataSaga() {
    yield takeEvery(GET_ASSET_CONFIG_DATA, getAssetConfigData);
}

function* getAssetListViewAttributes(
    action: GetAssetConfigDataAction
): Generator<
    | Promise<AssetListViewAttributesReceivedAction>
    | PutEffect<{ payload: AssetListViewAttributesReceivedAction["payload"]; type: string }>,
    void,
    AssetListViewAttributesReceivedAction["payload"]["data"]
> {
    try {
        let url = action.payload?.params?.useAlternateApi
            ? "/api/asset/table_attributes/"
            : "/api/proposed-assets-group/table_attributes/";
        let json = yield axios
            .get(url, { params: action.payload?.params })
            .then((response) => response.data);
        yield put(assetListViewAttributesReceived({ data: json }));
    } catch (e) {
        yield put(assetListViewAttributesReceived({ data: [] }));
        showError(e);
    }
}

export function* getAssetListViewAttributesSaga() {
    yield takeEvery(GET_ASSET_LIST_VIEW_ATTRIBUTES, getAssetListViewAttributes);
}

function* publishProposedAssets(action: PublishProposedAssetsAction) {
    try {
        yield action.payload.selectedRowIds.map((id: number, index: number) => {
            return axios.post(`/api/proposed-assets-group/${id}/publish/`).then((response) => {
                if (index === action.payload.selectedRowIds.length - 1) {
                    if (action.payload.afterPublish) {
                        action.payload.afterPublish();
                    }
                }
                return response.data;
            });
        });
    } catch (e) {
        showError(e);
    }
}

export function* publishProposedAssetsSaga() {
    yield takeEvery(PUBLISH_PROPOSED_ASSETS, publishProposedAssets);
}

function* deleteProposedAssets(action: DeleteProposedAssetsAction) {
    try {
        yield action.payload.selectedRowIds.map((id: number, index: number) => {
            return axios.delete(`/api/proposed-assets-group/${id}/`).then((response) => {
                if (index === action.payload.selectedRowIds.length - 1) {
                    if (action.payload.afterDelete) {
                        action.payload.afterDelete();
                    }
                }
                return response.data;
            });
        });
    } catch (e) {
        showError(e);
    }
}

export function* deleteProposedAssetsSaga() {
    yield takeEvery(DELETE_PROPOSED_ASSETS, deleteProposedAssets);
}
